import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import io from 'socket.io-client';
import { 
  ArrowLeft, 
  Send, 
  Car, 
  User, 
  Clock, 
  DollarSign, 
  MessageCircle,
  RefreshCw,
  Wifi,
  WifiOff
} from 'lucide-react';
import useAuth from '../../hooks/useAuth';
import toast from 'react-hot-toast';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 90vh;
  background: #202124;
  color: #ffffff;
  font-family: 'Clash Grotesk', sans-serif;
  width: 90%;
  margin: 0 auto;
`;

const Header = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
    @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #ffffff;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: none;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-1px);
  }
`;

const ChatInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const ChatTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: #ffffff;
`;

const ChatSubtitle = styled.p`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.6);
`;

const ConnectionDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.connected ? '#22C55E' : '#EF4444'};
`;

const RefreshButton = styled.button`
  background: rgba(244, 63, 96, 0.1);
  border: 1px solid rgba(244, 63, 96, 0.3);
  color: #F43F60;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(244, 63, 96, 0.2);
    transform: translateY(-1px);
  }
  
  &.loading {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const MessageBubble = styled(motion.div)`
  max-width: 85%;
  padding: 8px 15px;
  border-radius: 1rem;
  line-height: 1.4;
  font-size: 0.9rem;
  ${props => props.isUser ? `
    background: rgba(244, 63, 96, 0.1);
    color: #ffffff;
    align-self: flex-end;
  ` : `
    background: rgba(255, 255, 255, 0.05);
    color: #ffffff;
    align-self: flex-start;
  `}

  /* Markdown styling */
  h1, h2, h3, h4, h5, h6 {
    color: #ffffff;
    margin: 0.5rem 0;
    font-weight: 600;
  }

  h1 { font-size: 1.25rem; }
  h2 { font-size: 1.1rem; }
  h3 { font-size: 1rem; }

  p {
    margin: 0.5rem 0;
    font-size: 16px;
  }

  ul, ol {
    margin: 0.5rem 0;
    padding-left: 0;
    list-style: inside;
    list-style-type: disc;
  }

  li {
    margin: 0.25rem 0;
    font-size: 16px;
  }

  strong, b {
    font-weight: 600;
    color: #F43F60;
    font-size: 14px;
  }

  em, i {
    font-style: italic;
  }

  code {
    background: rgba(255, 255, 255, 0.1);
    padding: 0.2rem 0.4rem;
    border-radius: 0.25rem;
    font-family: 'Courier New', monospace;
    font-size: 0.85rem;
  }

  pre {
    background: rgba(0, 0, 0, 0.3);
    padding: 1rem;
    border-radius: 0.5rem;
    overflow-x: auto;
    margin: 0.5rem 0;
  }

  pre code {
    background: none;
    padding: 0;
  }

  blockquote {
    border-left: 3px solid #F43F60;
    padding-left: 1rem;
    margin: 0.5rem 0;
    color: rgba(255, 255, 255, 0.8);
  }

  a {
    color: #F43F60;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }
`;

const MessageTime = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 0.5rem;
  text-align: ${props => props.isUser ? 'right' : 'left'};
`;

const TypingIndicator = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  align-self: flex-start;
  margin-bottom: 1rem;
  
  .dot {
    width: 8px;
    height: 8px;
    background: #F43F60;
    border-radius: 50%;
    animation: typing 1.4s infinite ease-in-out;
  }
  
  .dot:nth-of-type(1) { animation-delay: 0s; }
  .dot:nth-of-type(2) { animation-delay: 0.2s; }
  .dot:nth-of-type(3) { animation-delay: 0.4s; }
  
  @keyframes typing {
    0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
    30% { transform: translateY(-4px); opacity: 1; }
  }
`;

const InputContainer = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1rem 1.5rem;
  flex-shrink: 0;
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  max-width: 800px;
  margin: 0 auto;
`;

const Input = styled.textarea`
  flex: 1;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 0.55rem 1rem;
  color: #ffffff;
  font-size: 16px;
  font-family: inherit;
  resize: none;
  outline: none;
  transition: border-color 0.2s ease;
  min-height: 44px;
  max-height: 120px;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    border-color: #F43F60;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SendButton = styled.button`
  background: #F43F60;
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  &:hover:not(:disabled) {
    background: #E03A56;
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: #666666;
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const InfoCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const InfoTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InfoContent = styled.div`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.4;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: rgba(255, 255, 255, 0.6);
`;

const UserChat = () => {
  const { sessionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Extract data from location state
  const {
    dealer,
    vehicle,
    offer,
    sessionInfo,
    chatStats
  } = location.state || {};

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadMessageHistory = async () => {
    try {
      setIsRefreshing(true);
      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/messages/user/session/${sessionId}/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch messages');
      }

      const data = await response.json();
      const formattedMessages = data.data.messages.map(msg => ({
        id: msg.id,
        text: msg.content,
        isUser: msg.type === 'user',
        timestamp: msg.timestamp,
        type: msg.type
      }));
      
      setMessages(formattedMessages);
    } catch (error) {
      console.error('Error loading message history:', error);
      toast.error('Failed to load message history');
    } finally {
      setIsRefreshing(false);
      setLoadingMessages(false);
    }
  };

  useEffect(() => {
    loadMessageHistory();
  }, [sessionId]);

  useEffect(() => {
    // Initialize socket connection
    const newSocket = io(process.env.REACT_APP_API_URL);
    setSocket(newSocket);

    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    if (token) {
      newSocket.emit('authenticate', token);
    }

    newSocket.on('authenticated', (data) => {
      setIsConnected(true);
      // Join the session room
      newSocket.emit('joinRoom',  sessionId );
    });

    newSocket.on('chatMessage', (message) => {
      setMessages(prev => [...prev, {
        id: Date.now(),
        text: message.content,
        isUser: message.type === 'user',
        timestamp: message.timestamp,
        type: message.type
      }]);
      setIsTyping(false);
    });

    newSocket.on('disconnect', () => {
      setIsConnected(false);
    });

    return () => {
      newSocket.disconnect();
    };
  }, [sessionId]);

  const handleSend = (e) => {
    e.preventDefault();
    if (!input.trim() || !socket || !isConnected) return;

    const messageData = {
      sessionId: sessionId,
      message: input,
      type: 'user'
    };

    socket.emit('chatMessage', messageData);
    
    // Add user message immediately
    setMessages(prev => [...prev, {
      id: Date.now(),
      text: input,
      isUser: true,
      timestamp: new Date().toISOString(),
      type: 'user'
    }]);

    setInput('');
    setIsTyping(true);
    
    // Auto-resize textarea
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    
    // Auto-resize textarea
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatPrice = (price) => {
    if (!price) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  if (loadingMessages) {
    return (
      <Container>
        <Header>
          <HeaderLeft>
            <BackButton onClick={() => navigate('/user/chats')}>
              <ArrowLeft size={20} />
            </BackButton>
            <ChatInfo>
              <ChatTitle>Loading...</ChatTitle>
            </ChatInfo>
          </HeaderLeft>
        </Header>
        <LoadingState>
          <div>Loading messages...</div>
        </LoadingState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
                      <BackButton onClick={() => navigate('/user/chats')}>
              <ArrowLeft size={20} />
            </BackButton>
          <ChatInfo>
            <ChatTitle>
              {sessionInfo?.hasDealerConnection ? 'Dealer Chat' : 'AI Assistant Chat'}
            </ChatTitle>
            <ChatSubtitle>
              Session: {sessionId.substring(0, 8)}... • {chatStats?.totalMessages || 0} messages
            </ChatSubtitle>
          </ChatInfo>
        </HeaderLeft>
        <HeaderRight>
          <StatusIndicator>
            <ConnectionDot connected={isConnected} />
            {isConnected ? 'Connected' : 'Disconnected'}
          </StatusIndicator>
          <RefreshButton 
            onClick={loadMessageHistory}
            className={isRefreshing ? 'loading' : ''}
            disabled={isRefreshing}
          >
            <RefreshCw size={16} />
          </RefreshButton>
        </HeaderRight>
      </Header>

      <MessagesContainer>
        {/* Session Info Cards */}
        {dealer && (
          <InfoCard>
            <InfoTitle>
              <User size={16} />
              Dealer Information
            </InfoTitle>
            <InfoContent>
              <strong>{dealer.name}</strong><br />
              {dealer.city} • {dealer.phone}<br />
              {dealer.email}
            </InfoContent>
          </InfoCard>
        )}

        {vehicle && (
          <InfoCard>
            <InfoTitle>
              <Car size={16} />
              Vehicle Information
            </InfoTitle>
            <InfoContent>
              <strong>{vehicle.year} {vehicle.make} {vehicle.model}</strong><br />
              {vehicle.trim} • {vehicle.mileage?.toLocaleString()} miles<br />
              {vehicle.condition} • {vehicle.transmission}<br />
              Price: {formatPrice(vehicle.price)}
            </InfoContent>
          </InfoCard>
        )}

        {offer && (
          <InfoCard>
            <InfoTitle>
              <DollarSign size={16} />
              Offer Information
            </InfoTitle>
            <InfoContent>
              <strong>Offer Amount: {formatPrice(offer.amount)}</strong><br />
              Status: {offer.status}<br />
              Created: {new Date(offer.createdAt).toLocaleDateString()}
            </InfoContent>
          </InfoCard>
        )}

        {/* Messages */}
        <AnimatePresence>
          {messages.map((message, index) => (
            <MessageBubble
              key={message.id || index}
              isUser={message.isUser}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {message.text}
              </ReactMarkdown>
              <MessageTime isUser={message.isUser}>
                {formatTime(message.timestamp)}
              </MessageTime>
            </MessageBubble>
          ))}
        </AnimatePresence>

        {isTyping && (
          <TypingIndicator
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="dot" />
            <div className="dot" />
            <div className="dot" />
          </TypingIndicator>
        )}

        <div ref={messagesEndRef} />
      </MessagesContainer>

      <InputContainer>
        <form onSubmit={handleSend}>
          <InputWrapper>
            <Input
              ref={inputRef}
              value={input}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              disabled={!isConnected}
              rows={1}
            />
            <SendButton type="submit" disabled={!input.trim() || !isConnected}>
              <Send size={18} />
            </SendButton>
          </InputWrapper>
        </form>
      </InputContainer>
    </Container>
  );
};

export default UserChat; 